import { useState, useEffect, useRef } from "react";
import ProductHeader from "../../Components/ProductsPages/ProductHeader";
import ProductsHeroSection from "../../Components/ProductsPages/ProductsHeroSection";
import ProductFooter from "../../Components/ProductsPages/ProductFooter";
import RequestDemo from "../../Components/ProductsPages/RequestDemo";
import QSleeveImage from "../../Images/videothumbnail.jpg";
import BackgroundGradient from "../../Images/BackgroundGradient.png";
import BackgroundGradientSmall from "../../Images/BackgroundGradientSmall.png";
import VideoComponent from "../../Components/VideoComponent";
import { KeyFeatures } from "../../Components/KeyFeatures";
import { ProductTable } from "../../Components/ProductsPages/ProductTable";
import QNTSIM from "../../../src/Images/Boxes/QNTSIM_P2.png";
import QNTSIM_P1 from "../../../src/Images/Boxes/QNTSIM_P1.png";
import { currentSite } from "../../Config/siteConfig";
export default function QNTSim() {
  const requestDemoRef = useRef(null);
  const architectureRef = useRef(null);

  const [backgroundImage, setBackgroundImage] = useState(``);

  useEffect(() => {
    const updateBackgroundImage = () => {
      if (window.innerWidth < 640) {
        setBackgroundImage(`url(${BackgroundGradientSmall})`);
      } else {
        setBackgroundImage(`url(${BackgroundGradient})`);
      }
    };

    updateBackgroundImage();

    window.addEventListener("resize", updateBackgroundImage);

    return () => window.removeEventListener("resize", updateBackgroundImage);
  }, []);

  const scrollToRequestDemo = () => {
    requestDemoRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToArchitecture = () => {
    architectureRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const keyFeatures = [
    "Configurable learning tool to support learning with protocol specific simulation and sweep mode.",
    "User-adjustable parameters to simulate realistic quantum network behavior, including attenuation, detector efficiency, and memory fidelity.",
    "QNTSim is a powerful conceptual and teaching tool for the next generation of quantum engineers and physicists.",
    "Intuitive GUI: Drag-and-drop network design with configurable control panels for parameter tuning and reruns. Easily simulate quantum communication workflows and QKD protocols with adjustable network parameters.",
    "Entanglement engine: Simulate entanglement generation, purification, and swapping with meet-in-the-middle BSM nodes.",
    "Physics-grounded outputs: Observe the impact of coherence time, detector efficiencies, fidelity degradation due to noise on entanglement distribution, and protocol-level performance indicators.",
    "Learn fundamentals quantum network components and their working in a cohesive manner.",
    "Interact with quantum networking protocols for entanglement generation and distribution.",
    "Test different network configurations with varying hardware parameters.",
    "Simulate quantum communication experiments without the need of any real quantum hardware.",
    "Design and build novel quantum network protocols.",
  ];

  const specifications1 = [
           ["Specification", "Description"],
           [
             "Configurable Learning Tool",
             "Protocol-specific: overview, statistics, inferences, and sweep mode",
           ],
           ["Supported Protocols", "E2E, CHSH Test, E91, BBM92"],
           ["Entanglement Support", "Generation, Purification, Swapping"],
           ["Network Topologies", "Customizable up to 15 nodes"],
           [
             "Physical Layer Models",
             "Quantum/Classical channels, detectors, sources, memories",
           ],
           [
             "Tunable Parameters",
             "Channel length, attenuation, detector efficiency, memory fidelity",
           ],
           ["Simulation Type", "Discrete-event simulation"],
           ["Architecture", "Modular controller-driven simulation engine"],
           [
             "Output",
             "Entangled pairs, S-value, key metrics, simulation time, fidelity tracking",
           ],
         ]



  return (
    <div className="w-full  flex flex-col gap-0 items-center justify-center bg-primary7">
      <div
        className="w-full width flex flex-col gap-0 items-center justify-center bg-contain bg-repeat"
        style={{ backgroundImage: backgroundImage }}
      >
        <ProductHeader scrollToRequestDemo={scrollToRequestDemo} />
        <div className="w-full h-[25rem] ">
          <ProductsHeroSection
            title1="Explore Quantum Networks"
            title2="Through Simulation"
            content="Understand entanglement, quantum protocols, and network behavior with a hands-on simulator designed for academia, labs, and next-gen quantum communication engineers."
            scrollToArchitecture={scrollToArchitecture}
          />
        </div>

        <div className="w-full bg-primary7 width md:px-10  flex flex-col items-center justify-center pb-10 gap-1">
          <div className="w-[90%] text-center md:text-left text-[1.5rem] md:text-[2rem] 2xl:text-[2.3rem] font-RobotoBold text-white leading-tight px-3 ">
            <span className="wordGradient">Quantum Network Simulator</span>
          </div>

          <div className="w-[90%] h-full   grid grid-cols-1 xl:grid-cols-2  place-items-start gap-20 p-3">
            <div className="w-full  flex flex-col items-center justify-center  gap-1">
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                QNTSim is the first network simulation platform in India
                tailored for academia and research training. It helps students,
                instructors, and laboratories visualize complex quantum
                phenomena such as entanglement and quantum communication through
                an intuitive and user-guided learning environment. Designed to
                be easy to learn and use, QNTSim offers customizable network
                setups and enables users to explore, test, and understand
                quantum communication protocols.
              </div>
              {/* <div className="w-full text-white text-center sm:text-left font-RobotoRegular text-xs md:text-sm px-3">
                Building upon the technical analysis of the performance and
                quality of entangled photons generated, the Kit enables
                experimentation of Quantum Key Distribution setup with BBM92
                protocol. This QKD platform facilitates realising Key
                Distribution, Quantum Bit Error Rate calculations along with
                analysis of signal-to-noise ratio all in one inclusive unit. The
                product is designed as an Integrated experimental QKD system
                facilitating study of quantum entanglement phenomena and its
                application for Quantum Key Distribution.
              </div> */}

              <div className="w-full sm:pl-10 flex items-start justify-start">
                <KeyFeatures features={keyFeatures.slice(0, 3)} />
              </div>
              <ProductTable title="Specifications" data={specifications1} />

              <div className="w-full  flex flex-col items-start justify-start pt-10">
                <img
                  src={QNTSIM_P1}
                  alt="QNTSIM"
                  className="w-full h-full object-contain rounded-lg md:rounded-2xl bg-white"
                />
                <p className="w-full lg:max-w-[600px]  text-center text-xs italic text-white/70 mt-2">
                  Figure 1: QNTSIM Architecture
                </p>
              </div>
            </div>

            <div className="w-full  flex flex-col items-start justify-center  gap-1">
              <KeyFeatures
                title="Key Features"
                features={keyFeatures.slice(3, 6)}
              />
              <KeyFeatures
                title="Experimentation Outcomes"
                features={keyFeatures.slice(7, 11)}
              />

              <div className="w-full  flex flex-col items-start justify-start ">
                <img
                  src={QNTSIM}
                  alt="QNTSIM"
                  className="w-full h-full object-contain rounded-lg md:rounded-2xl"
                />
                <p className="w-full lg:max-w-[600px] text-center text-xs italic text-white/70 mt-2">
                  Figure 2: The visual representation of Quantum Network.
                </p>
              </div>

              {/* <KeyFeatures features={keyFeatures.slice(0, 3)} /> */}
              {/* <div className="w-full  flex flex-col items-start justify-center mt-5">
                <img
                  src={EPSQKD2}
                  alt="EPSQKD1"
                  className="w-full h-full object-contain rounded-lg md:rounded-2xl"
                />
                <p className="w-full text-center text-xs italic text-white/70 mt-2">
                  Figure 1: Entanglement-based QKD
                </p>
              </div> */}
            </div>
          </div>
          {/* <div className="w-[90%] h-full px-12 grid grid-cols-1 sm:grid-cols-2  place-items-start gap-10 ">
            <ProductTable title="Specifications" data={specifications1} />
          </div> */}
        </div>
        {currentSite === "site2" && (
          <VideoComponent
            image={QSleeveImage}
            video={"https://thequantum.ai/videos/QNTSimVideo.mp4"}
          />)}
        {currentSite === "site1" && (
          <VideoComponent
            image={QSleeveImage}
            video={"https://qulabs.ai/videos/QNTSimVideo.mp4"}
          />
        )}
        <div
          className="w-full bg-primary7 width flex flex-col items-center justify-center gap-2 "
          ref={requestDemoRef}
        >
          <RequestDemo />
        </div>

        <ProductFooter />
      </div>
    </div>
  );
}



